import React from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';
import { Bar, Doughnut, Pie, Radar } from 'react-chartjs-2';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

const ChartCard = ({ title, type, data, options }) => {
  const formatChartData = (rawData) => {
    if (!rawData || !Array.isArray(rawData)) {
      return {
        labels: [],
        datasets: [{
          data: [],
          backgroundColor: [],
          borderColor: [],
          borderWidth: 1
        }]
      };
    }
    
  // 🟢 Filter hanya item yang punya nilai salah satu field
  const filteredData = rawData.filter(item =>
    item &&
    (item.land_ownership ||
     item.house_ownership ||
     item.gender ||
     item.education ||
     item.occupation ||
     item.monthly_income ||
     item.name)
  );

    const colors = [
      'rgba(59, 130, 246, 0.6)', 'rgba(16, 185, 129, 0.6)', 
      'rgba(245, 158, 11, 0.6)', 'rgba(239, 68, 68, 0.6)', 
      'rgba(139, 92, 246, 0.6)', 'rgba(6, 182, 212, 0.6)', 
      'rgba(132, 204, 22, 0.6)', 'rgba(249, 115, 22, 0.6)', 
      'rgba(236, 72, 153, 0.6)', 'rgba(99, 102, 241, 0.6)'
    ];

    const labels = rawData.map(item => {
      // Format labels based on data type
      // Filter data agar hanya ambil yang terdefinisi
      if (item.land_ownership) {
        const landOwnershipMap = {
          'MILIK SENDIRI': 'Milik Sendiri',
          'BUKAN MILIK SENDIRI': 'Bukan Milik Sendiri',
          'TANAH NEGARA': 'Tanah Negara'
        };
        return landOwnershipMap[item.land_ownership];
      }
      if (item.house_ownership) {
        const houseOwnershipMap = {
          'MILIK SENDIRI': 'Milik Sendiri',
          'BUKAN MILIK SENDIRI': 'Bukan Milik Sendiri',
          'KONTRAK/SEWA': 'Kontrak/Sewa'
          };
        return houseOwnershipMap[item.house_ownership];
      }
      if (item.gender) {
        return item.gender === 'LAKI-LAKI' ? 'Laki-laki' : 'Perempuan';
      }
      if (item.education) {
        const educationMap = {
          'TIDAK PUNYA IJAZAH': 'Tidak Punya Ijazah',
          'SD/SEDERAJAT': 'SD/Sederajat',
          'SMP/SEDERAJAT': 'SMP/Sederajat',
          'SMA/SEDERAJAT': 'SMA/Sederajat',
          'D1/D2/D3': 'D1/D2/D3',
          'D4/S1': 'D4/S1'
        };
        return educationMap[item.education] || item.education;
      }
      if (item.occupation) {
        const occupationMap = {
          'PNS': 'PNS',
          'BUMN/D': 'BUMN/D',
          'TNI/POLRI': 'TNI/POLRI',
          'KARYAWAN': 'Karyawan',
          'WIRAUSAHA': 'Wirausaha',
          'PETANI': 'Petani',
          'BURUH HARIAN': 'Buruh Harian',
          'NELAYAN': 'Nelayan',
          'OJEK/SOPIR': 'Ojek/Sopir',
          'PRAMUWISMA': 'Pramuwisma',
          'LANSIA/IRT': 'Lansia/IRT',
          'TUKANG/MONTIR': 'Tukang/Montir',
          'HONORER': 'Honorer',
          'PENSIUNAN': 'Pensiunan',
          'LAINNYA': 'Lainnya',
          'TIDAK BEKERJA': 'Tidak Bekerja'
        };
        return occupationMap[item.occupation] || item.occupation;
      }
      if (item.monthly_income) {
        const incomeMap = {
          '0 - 1,2 JUTA': '0 - 1,2 Juta',
          '1,3 - 1,8 JUTA': '1,3 - 1,8 Juta',
          '1,9 - 2,1 JUTA': '1,9 - 2,1 Juta',
          '2,2 - 2,6 JUTA': '2,2 - 2,6 Juta',
          '2,7 - 3,1 JUTA': '2,7 - 3,1 Juta',
          '3,2 - 3,6 JUTA': '3,2 - 3,6 Juta',
          '3,7 - 4,2 JUTA': '3,7 - 4,2 Juta',
          '> 4,2 JUTA': '> 4,2 Juta'
        };
        return incomeMap[item.monthly_income] || item.monthly_income;
      }
      return item.name || 'Unknown';
    });

    const values = filteredData.map(item => item.count || 0);
    const backgroundColors = values.map((_, index) => colors[index % colors.length]);

    return {
      labels,
      datasets: [{
        data: values,
        backgroundColor: backgroundColors,
        borderColor: backgroundColors.map(color => color + '80'),
        borderWidth: 1
      }]
    };
  };

  const chartData = formatChartData(data);

  const renderChart = () => {
    const defaultOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
        },
        title: {
          display: true,
          text: title
        }
      }
    };

    const finalOptions = { ...defaultOptions, ...options };

    switch (type) {
      case 'bar':
        return <Bar data={chartData} options={finalOptions} />;
      case 'doughnut':
        return <Doughnut data={chartData} options={finalOptions} />;
      case 'pie':
        return <Pie data={chartData} options={finalOptions} />;
      case 'radar':
        return <Radar data={chartData} options={finalOptions} />;
      default:
        return <Bar data={chartData} options={finalOptions} />;
    }
  };

  return (
    <div className="card h-100">
      <div className="card-header">
        <h6 className="mb-0">
          <i className="fas fa-chart-bar me-2"></i>
          {title}
        </h6>
      </div>
      <div className="card-body">
        <div style={{ height: '300px' }}>
          {renderChart()}
        </div>
      </div>
    </div>
  );
};

export default ChartCard; 